import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores'
import apiClient from '@/api/client'

const routes: RouteRecordRaw[] = [
  {
    path: '/setup',
    name: 'setup',
    component: () => import('@/views/auth/SetupView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
 
      {
        path: 'administration',
        name: 'administration',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'administration.operators' },
        children: [
          {
            path: 'operators',
            name: 'administration.operators',
            component: () => import('@/views/administration/OperatorsView.vue'),
          },
          {
            path: 'operators/new',
            name: 'administration.operators.create',
            component: () => import('@/views/administration/OperatorEditView.vue'),
          },
          {
            path: 'operators/:operatorId',
            name: 'administration.operators.edit',
            component: () => import('@/views/administration/OperatorEditView.vue'),
          },
          {
            path: 'organizations',
            name: 'administration.organizations',
            component: () => import('@/views/administration/OrganizationsView.vue'),
          },
          {
            path: 'organizations/new',
            name: 'administration.organizations.create',
            component: () => import('@/views/administration/OrganizationEditView.vue'),
          },
          {
            path: 'organizations/:organizationId',
            name: 'administration.organizations.edit',
            component: () => import('@/views/administration/OrganizationEditView.vue'),
          },
          {
            path: 'instance-definitions',
            name: 'administration.instance-definitions',
            component: () => import('@/views/administration/InstanceDefinitionsView.vue'),
          },
          {
            path: 'instance-definitions/new',
            name: 'administration.instance-definitions.create',
            component: () => import('@/views/administration/InstanceDefinitionEditView.vue'),
          },
          {
            path: 'instance-definitions/:instanceDefinitionId',
            name: 'administration.instance-definitions.edit',
            component: () => import('@/views/administration/InstanceDefinitionEditView.vue'),
          },
        ],
      },
      {
        path: 'infrastructure',
        name: 'infrastructure',
        component: () => import('@/layouts/SectionLayout.vue'),
        redirect: { name: 'infrastructure.deployment-targets' },
        children: [
          {
            path: 'targets',
            name: 'infrastructure.deployment-targets',
            component: () => import('@/views/infrastructure/DeploymentTargetsView.vue'),
          },
          {
            path: 'targets/new',
            name: 'infrastructure.deployment-targets.create',
            component: () => import('@/views/infrastructure/DeploymentTargetEditView.vue'),
          },
          {
            path: 'targets/:deploymentTargetId',
            name: 'infrastructure.deployment-targets.edit',
            component: () => import('@/views/infrastructure/DeploymentTargetEditView.vue'),
          },
          {
            path: 'instances',
            name: 'infrastructure.instances',
            component: () => import('@/views/infrastructure/InstancesView.vue'),
          },
          {
            path: 'instances/new',
            name: 'infrastructure.instances.create',
            component: () => import('@/views/infrastructure/InstanceEditView.vue'),
          },
          {
            path: 'instances/:instanceId',
            name: 'infrastructure.instances.edit',
            component: () => import('@/views/infrastructure/InstanceEditView.vue'),
          },
          {
            path: 'source-repositories',
            name: 'infrastructure.source-repositories',
            component: () => import('@/views/infrastructure/SourceRepositoriesView.vue'),
          },
          {
            path: 'source-repositories/new',
            name: 'infrastructure.source-repositories.create',
            component: () => import('@/views/infrastructure/SourceRepositoryEditView.vue'),
          },
          {
            path: 'source-repositories/:sourceRepositoryId',
            name: 'infrastructure.source-repositories.edit',
            component: () => import('@/views/infrastructure/SourceRepositoryEditView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

async function isAppConfigured(): Promise<boolean> {
  try {
    const data = await apiClient.setupStatusList()
    return data.isConfigured === true
  } catch {
    return true
  }
}

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)

  if (requiresAuth && !authStore.isAuthenticated) {
    const configured = await isAppConfigured()
    next({ name: configured ? 'login' : 'setup', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
  } else if (to.name === 'setup') {
    const configured = await isAppConfigured()
    if (authStore.isAuthenticated || configured) {
      next({ name: authStore.isAuthenticated ? 'dashboard' : 'login' })
    } else {
      next()
    }
  } else if (requiresAuth && authStore.isAuthenticated) {
    try {
      await authStore.ensureProfile()
      next()
    } catch {
      const configured = await isAppConfigured()
      next({ name: configured ? 'login' : 'setup', query: { redirect: to.fullPath } })
    }
  } else {
    next()
  }
})

export default router
